import styles from "@/styles/ai-search.module.scss";
import { useState, useRef, useEffect, Fragment } from "react";
import Image from "next/image";
import { Input, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
const { TextArea } = Input;
import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const imageDomain = publicRuntimeConfig.imageDomain;
const apiDomain = publicRuntimeConfig.apiDomain;
import GoodsAIRecommend from "./GoodsAIRecommend";
import { getCookie } from "@/utils/cookie";
import { matomoSetCustomDimension } from '@/utils/matomoEvent';

interface StageData {
  stage: "analyzing" | "product" | "summary" | "recommend_words";
  content: string | object; // product 和 recommend_words 的 content 是对象，其他是字符串
}

interface Message {
  role: "user" | "assistant";
  content: string;
  stages?: StageData[];
}

interface AiSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AiSearch({ isOpen, onClose }: AiSearchProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isNewChatHovered, setIsNewChatHovered] = useState(false);
  const [conversationId, setConversationId] = useState<string>("");
  const [showNewChatDivider, setShowNewChatDivider] = useState(false);
  const [refreshHoverKey, setRefreshHoverKey] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false); // 跟踪关闭动画状态
  const [activeWelcomeTag, setActiveWelcomeTag] = useState<string>(""); // 记录点击的欢迎标签
  // 存储每条消息的 analyzing 阶段展开状态，key 为消息索引，确保每条消息独立控制
  const [expandedAnalyzingStages, setExpandedAnalyzingStages] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const aiSearchBoxRef = useRef<HTMLDivElement>(null);
  const isUserNearBottomRef = useRef<boolean>(true); // 跟踪用户是否在底部附近
  const shouldAutoScrollRef = useRef<boolean>(true); // 控制是否应该自动滚动
  const lastUserMessageIndexRef = useRef<number>(-1); // 跟踪最后发送的用户消息索引

  // 建议话题列表
  const suggestedTopics = [
    "Find summer deals under $50",
    "Find toys for a 3-year-old",
    "Show me today's best sellers",
    "Recommend must-have home essentials"
  ];

  // 欢迎标签配置
  const welcomeTagConfig = [
    {
      tag: "Find products",
      reply: "What product are you looking for today?\n\nYou can describe:\n• budget\n• style\n• brand\n• how you'll use it",
      iconUrl: `${imageDomain}/ai-chat/icon-search.svg`
    },
    {
      tag: "Compare options",
      reply: "Need help choosing between products?\n\nShare product links, IDs, or titles, and I’ll compare them for you.",
      iconUrl: `${imageDomain}/ai-chat/icon-compare.svg`
    },
    {
      tag: "Gift ideas",
      reply: "Who is the gift for?\n\nYou can include:\n• age\n• interests\n• occasion\n• budget",
      iconUrl: `${imageDomain}/ai-chat/icon-gift.svg`
    }
  ];

  // 关闭弹窗时不清空内容，只关闭弹窗
  const handleClose = () => {
    // 如果正在关闭，直接返回，避免重复触发
    if (isClosing) return;
    
    // 取消正在进行的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setActiveWelcomeTag("");
    setIsClosing(true);
    
    // 等待动画完成后调用 onClose（动画时长为 0.3s）
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // 点击建议话题
  const handleSuggestedTopicClick = (topic: string) => {
    handleSendWithQuery(topic);
    matomoSetCustomDimension(42, topic);
  };

  // 点击欢迎标签，模拟 AI 回复
  const handleWelcomeTagClick = (item: { tag: string; reply: string }) => {
    setActiveWelcomeTag(item.tag);
    const assistantMessage: Message = {
      role: "assistant",
      content: item.reply,
    };
    setMessages((prev) => [...prev, assistantMessage]);
    // 延迟滚动到底部，确保 DOM 更新完成
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // 使用指定查询发送消息
  const handleSendWithQuery = async (query: string, regenerate: boolean = false) => {
    if (!query.trim() || isLoading) return;

    // 发送新消息时隐藏新聊天提示
    setShowNewChatDivider(false);

    const userMessage: Message = {
      role: "user",
      content: query.trim(),
    };

    setMessages((prev) => {
      const newMessages = [...prev, userMessage];
      // 记录用户消息的索引（发送后的索引）
      lastUserMessageIndexRef.current = newMessages.length - 1;
      
      // 立即滚动到用户消息顶部
      scrollToMessageTop(newMessages.length - 1);
      
      return newMessages;
    });
    setInputValue("");
    setIsLoading(true);

    // 添加一个空的助手消息，用于流式更新
    const assistantMessage: Message = {
      role: "assistant",
      content: "",
      stages: [],
    };
    setMessages((prev) => {
      const newMessages = [...prev, assistantMessage];
      
      // 助手消息添加后，再次确保用户消息在顶部（因为助手消息有最小高度）
      const userMessageIndex = lastUserMessageIndexRef.current;
      if (userMessageIndex >= 0) {
        scrollToMessageTop(userMessageIndex, true); // 传入 true 表示需要检查差异
      }
      
      return newMessages;
    });

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // 在 try 块外定义，以便在 finally 中访问
    const analyzingTimeouts: ReturnType<typeof setTimeout>[] = []; // 保存所有 analyzing 延迟的 timeout ID

    try {
      const customerId = getCookie('userCustomerId') || 0;
      
      const response = await fetch(`${apiDomain}/ai-product-front/recommend/stream`, {

      // const response = await fetch(`/api/ai-search/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: userMessage.content,
          customer_id: customerId,
          conversation_id: conversationId,
          regenerate: regenerate
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorMessage = "System is busy, please try again later";
        try {
          const errorData = await response.json();
          const error = new Error(errorData.message || errorMessage) as any;
          error.status = response.status || 500;
          error.errorData = errorData;
          throw error;
        } catch (parseError: any) {
          // 如果解析JSON失败，使用默认错误
          // parseError 可能是 JSON 解析错误，也可能是上面抛出的错误对象
          if (parseError.message && parseError.status) {
            // 这是上面抛出的错误对象，直接重新抛出
            throw parseError;
          }
          // 这是 JSON 解析错误，也要抛出包含 status 的错误对象
          const error = new Error(parseError.errorData?.message || errorMessage) as any;
          error.status = response.status || 500;
          error.errorData = parseError.errorData || null;
          throw error;
        }
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("No reader available");
      }

      let accumulatedContent = "";
      let buffer = "";
      let stages: StageData[] = [];
      let analyzingStageIndex = 0; // 跟踪 analyzing stage 的序号
      const pendingAnalyzingStages: StageData[] = []; // 保存所有待显示的 analyzing stage
      
      // analyzing stage 延迟配置
      const totalAnalyzingDelay = 13000; // 总延迟时间（毫秒），13秒
      const delayedAnalyzingCount = 4; // 需要延迟的 analyzing stage 数量
      const analyzingDelayInterval = Math.floor(totalAnalyzingDelay / delayedAnalyzingCount); // 每条之间的延迟间隔

      // 使用requestAnimationFrame优化更新频率
      let lastUpdateTime = 0;
      const updateInterval = 50; // 每50ms更新一次UI

      const updateMessage = (content: string, newStages?: StageData[]) => {
        const stagesToUse = newStages || stages;
        setMessages((prev) => {
          const newMessages = [...prev];
          if (
            newMessages.length > 0 &&
            newMessages[newMessages.length - 1].role === "assistant"
          ) {
            newMessages[newMessages.length - 1] = {
              ...newMessages[newMessages.length - 1],
              content: content,
              stages: stagesToUse,
            };
          }
          return newMessages;
        });
      };

      // 取消所有 analyzing 延迟并立即显示
      const cancelAnalyzingDelays = () => {
        // 清除所有延迟的 timeout
        analyzingTimeouts.forEach(timeout => clearTimeout(timeout));
        analyzingTimeouts.length = 0;
        
        // 立即显示所有待显示的 analyzing stage
        if (pendingAnalyzingStages.length > 0) {
          pendingAnalyzingStages.forEach(stageData => {
            stages.push(stageData);
          });
          pendingAnalyzingStages.length = 0;
          updateMessage(accumulatedContent, [...stages]);
        }
      };

      // 添加 stage 到数组的函数
      const addStage = (stageData: StageData, delay: number = 0) => {
        if (delay > 0) {
          // 保存待显示的 stage
          pendingAnalyzingStages.push(stageData);
          
          // 设置延迟显示
          const timeoutId = setTimeout(() => {
            const index = pendingAnalyzingStages.indexOf(stageData);
            if (index > -1) {
              pendingAnalyzingStages.splice(index, 1);
            }
            stages.push(stageData);
            updateMessage(accumulatedContent, [...stages]);
          }, delay);
          
          analyzingTimeouts.push(timeoutId);
        } else {
          stages.push(stageData);
          // 非延迟的 stage 也需要更新 UI
          updateMessage(accumulatedContent, [...stages]);
        }
      };

      // 处理 JSON 数据的公共函数
      const processJsonData = (content: string): boolean => {
        try {
          const jsonData = JSON.parse(content);
          
          // 检查是否有 conversation_id 字段（与 stage、content 同级）
          if (jsonData.conversation_id) {
            setConversationId(jsonData.conversation_id);
          }
          
          // 检查是否有 stage 字段
          if (jsonData.stage && jsonData.content !== undefined) {
            // 如果是 product 或 recommend_words stage，content 是 JSON 字符串，需要解析成对象
            let stageContent: string | object = jsonData.content;
            if ((jsonData.stage === "product" || jsonData.stage === "recommend_words") && typeof jsonData.content === "string") {
              try {
                stageContent = JSON.parse(jsonData.content);
              } catch (e) {
                // 如果解析失败，保持原样
                stageContent = jsonData.content;
              }
            }
            
            const stageData: StageData = {
              stage: jsonData.stage,
              content: stageContent,
            };
            
            // 如果是 analyzing stage，第三条及以后的延迟
            if (jsonData.stage === "analyzing") {
              // 第一条和第二条立即显示，从第三条开始每条延迟 analyzingDelayInterval 毫秒
              // 第三条延迟 analyzingDelayInterval，第四条延迟 2*analyzingDelayInterval，以此类推
              const delay = analyzingStageIndex >= 2 ? (analyzingStageIndex - 1) * analyzingDelayInterval : 0;
              analyzingStageIndex++;
              addStage(stageData, delay);
            } else {
              // 如果是 product stage，取消所有 analyzing 延迟并立即显示
              if (jsonData.stage === "product") {
                cancelAnalyzingDelays();
              }
              // 其他 stage 立即显示
              addStage(stageData);
            }
            
            accumulatedContent += typeof stageContent === "string" 
              ? stageContent + "\n" 
              : JSON.stringify(stageContent) + "\n";
            return true;
          } else if (jsonData.content || jsonData.text || jsonData.message) {
            // 如果有content字段，追加content
            const textContent = jsonData.content || jsonData.text || jsonData.message;
            accumulatedContent += textContent + "\n";
            return true;
          } else if (typeof jsonData === "string") {
            accumulatedContent += jsonData + "\n";
            return true;
          } else {
            accumulatedContent += JSON.stringify(jsonData) + "\n";
            return true;
          }
        } catch {
          // 如果不是JSON，直接追加文本
          accumulatedContent += content + "\n";
          return false;
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // 流结束时，取消所有延迟并立即显示所有 analyzing stage
          cancelAnalyzingDelays();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // 保留最后一个不完整的行

        for (const line of lines) {
          let content = line.trim();
          if (!content) continue;

          // 处理event标记（如 event: done）
          if (content.startsWith("event: ")) {
            if (content.slice(7).trim() === "done") {
              // 收到 done 事件，取消所有延迟并立即显示所有 analyzing stage
              cancelAnalyzingDelays();
              continue;
            }
            continue;
          }

          // 处理SSE格式（data: 开头）
          if (content.startsWith("data: ")) {
            content = content.slice(6).trim();
          }

          // 处理结束标记
          if (content === "[DONE]" || content === "end") {
            // 收到结束标记，取消所有延迟并立即显示所有 analyzing stage
            cancelAnalyzingDelays();
            continue;
          }

          // 处理 JSON 数据
          processJsonData(content);

          // 节流更新UI，避免过于频繁的渲染
          const now = Date.now();
          if (now - lastUpdateTime >= updateInterval) {
            updateMessage(accumulatedContent, stages);
            lastUpdateTime = now;
          }
        }
      }

      // 处理剩余的buffer内容
      if (buffer.trim()) {
        let content = buffer.trim();
        
        // 处理 event 标记
        if (content.startsWith("event: ")) {
          if (content.slice(7).trim() === "done") {
            // 收到 done 事件，取消所有延迟并立即显示所有 analyzing stage
            cancelAnalyzingDelays();
          }
        } else {
          // 处理 data: 前缀
          if (content.startsWith("data: ")) {
            content = content.slice(6).trim();
          }
          
          // 处理结束标记
          if (content === "[DONE]" || content === "end") {
            // 收到结束标记，取消所有延迟并立即显示所有 analyzing stage
            cancelAnalyzingDelays();
          } else if (content) {
            // 处理有效内容
            processJsonData(content);
          }
        }
      }

      // 最终更新一次，确保所有内容都显示
      updateMessage(accumulatedContent, stages);
    } catch (error: any) {
      if (error.name === "AbortError") {
        // Request aborted
      } else {
        console.error("Error:", error);
        // 更新最后一条消息显示错误
        setMessages((prev) => {
          const newMessages = [...prev];
          if (
            newMessages.length > 0 &&
            newMessages[newMessages.length - 1].role === "assistant"
          ) {
            const errorMessage = error.message || "Sorry, an error occurred. Please try again later.";
            
            newMessages[newMessages.length - 1] = {
              ...newMessages[newMessages.length - 1],
              content: errorMessage,
            };
          }
          return newMessages;
        });
      }
    } finally {
      // 清理所有 analyzing 延迟的 timeout
      analyzingTimeouts.forEach((timeout: ReturnType<typeof setTimeout>) => clearTimeout(timeout));
      analyzingTimeouts.length = 0;
      
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  // 检查用户是否在底部附近（距离底部50px以内）
  const checkIfNearBottom = (): boolean => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom <= 50; // 50px 的阈值
  };

  // 滚动到指定消息的顶部（预留12px间距）
  const scrollToMessageTop = (messageIndex: number, checkDifference: boolean = false) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        
        const messageElement = container.querySelector(`[data-message-index="${messageIndex}"]`) as HTMLElement;
        if (messageElement) {
          const containerRect = container.getBoundingClientRect();
          const messageRect = messageElement.getBoundingClientRect();
          const currentScrollTop = container.scrollTop;
          const messageTopRelativeToContainer = messageRect.top - containerRect.top + currentScrollTop;
          const targetScrollTop = messageTopRelativeToContainer - 12; // 预留12px间距
          
          // 如果需要检查差异，且差异小于10px，则不滚动
          if (checkDifference && Math.abs(container.scrollTop - targetScrollTop) <= 10) {
            return;
          }
          
          // 平滑滚动到用户消息顶部
          container.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
          
          // 更新状态：用户不在底部附近
          isUserNearBottomRef.current = false;
        }
      });
    });
  };

  // 自动滚动到底部（只在用户接近底部时）
  useEffect(() => {
    // 如果用户不在底部附近，不自动滚动
    if (!shouldAutoScrollRef.current || !isUserNearBottomRef.current) {
      return;
    }
    
    // 延迟一下，确保 DOM 更新完成
    const timer = setTimeout(() => {
      // 再次检查用户是否还在底部附近（可能在延迟期间用户滚动了）
      if (shouldAutoScrollRef.current && checkIfNearBottom()) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [messages]);

  // 监听滚动事件，更新用户是否在底部附近的状态
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      isUserNearBottomRef.current = checkIfNearBottom();
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);


  // 当显示新聊天提示时，自动滚动到底部
  useEffect(() => {
    if (showNewChatDivider) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [showNewChatDivider]);

  // 当 isOpen 变为 true 时，重置 isClosing 状态
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  // 阻止弹窗滚动事件冒泡到主页面
  useEffect(() => {
    if (!isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      // 检查事件是否发生在弹窗内
      const target = e.target as HTMLElement;
      const aiSearchBox = aiSearchBoxRef.current;

      if (aiSearchBox && aiSearchBox.contains(target)) {
        // 事件发生在弹窗内，检查是否需要阻止冒泡
        const messagesContainer = messagesContainerRef.current;
        if (messagesContainer) {
          const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
          const isScrollingUp = e.deltaY < 0;
          const isScrollingDown = e.deltaY > 0;

          // 如果滚动到顶部且继续向上滚动，或滚动到底部且继续向下滚动，阻止冒泡
          if (
            (scrollTop <= 0 && isScrollingUp) ||
            (scrollTop + clientHeight >= scrollHeight - 1 && isScrollingDown) ||
            scrollHeight <= clientHeight
          ) {
            e.stopPropagation();
            e.preventDefault();
          }
        } else {
          // 如果消息容器不存在，阻止所有弹窗内的滚动事件冒泡
          e.stopPropagation();
          e.preventDefault();
        }
      }
      // 如果事件不在弹窗内，允许正常滚动主页面
    };

    // 使用捕获阶段来拦截滚动事件
    document.addEventListener("wheel", handleWheel, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, [isOpen]);

  // 处理消息列表滚动，防止滚动穿透
  const handleMessagesScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollingUp = e.deltaY < 0;
    const isScrollingDown = e.deltaY > 0;

    // 更新用户是否在底部附近的状态
    isUserNearBottomRef.current = checkIfNearBottom();

    // 如果滚动到顶部，且继续向上滚动，阻止事件冒泡
    if (scrollTop <= 0 && isScrollingUp) {
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    // 如果滚动到底部，且继续向下滚动，阻止事件冒泡
    if (scrollTop + clientHeight >= scrollHeight - 1 && isScrollingDown) {
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    // 如果内容没有超出容器高度，不需要滚动，阻止事件冒泡
    if (scrollHeight <= clientHeight) {
      e.stopPropagation();
      e.preventDefault();
      return false;
    }
  };

  // 发送消息
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    const prefix = activeWelcomeTag ? `${activeWelcomeTag}\n` : "";
    const finalQuery = `${prefix}${inputValue.trim()}`;
    setActiveWelcomeTag("");
    await handleSendWithQuery(finalQuery);
  };

  // 处理回车键
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 清空对话
  const handleClear = () => {
    // 不清空消息，只显示新聊天提示
    // 只有当存在 conversationId 时才显示提示
    if (conversationId) {
      setShowNewChatDivider(true);
    }
    setConversationId("");
    setActiveWelcomeTag("");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* 蒙层 */}
      <div
        className={styles["ai-search-mask"]}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* 对话窗口 */}
      <div
        ref={aiSearchBoxRef}
        className={`${styles["ai-search-box"]} ${isClosing ? styles["box-closing"] : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div 
          className={styles["ai-search-header"]}
          style={{
            backgroundImage: `url(${imageDomain}/ai-chat/ai-chat-bg.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className={styles["ai-search-title"]}>
            <Image
              className={styles["ai-robot-icon"]}
              src={`${imageDomain}/ai-chat/ai-robot.svg`}
              alt="AI Search"
              width={24}
              height={24}
            />
            <div className={styles["ai-search-title-text"]}>Need product help? Ask me!</div>
          </div>
          <Image
            src={`${imageDomain}/ai-chat/close.svg`}
            alt="Close"
            width={16}
            height={16}
            onClick={handleClose}
            className={styles["close-btn"]}
          />
        </div>

        {/* 消息列表 */}
        <div
          ref={messagesContainerRef}
          className={styles["ai-search-messages"]}
          onWheel={handleMessagesScroll}
        >
          <div className={styles["welcome-state"]}>
            <div className={styles["welcome-card"]}>
              <div className={styles["welcome-greeting"]}>Hi mate!</div>
              <div className={styles["welcome-description"]}>
                Share your product needs with me, <br></br>I&apos;ll suggest the best options instantly.
              </div>
            </div>
            <div className={styles["welcome-tags"]}>
              {welcomeTagConfig.map((item, index) => (
                <div
                  key={index}
                  className={styles["welcome-tag"]}
                  onClick={() => handleWelcomeTagClick(item)}
                >
                  <Image
                    src={`${item.iconUrl}`}
                    alt={item.tag}
                    className={styles["welcome-tag-icon"]}
                    width={16}
                    height={16}
                  />
                  {item.tag}
                </div>
              ))}
            </div>
            <div className={styles["suggested-topics-section"]}>
              <div className={styles["suggested-topics-title"]}>
                Things you might be into
              </div>
              <div className={styles["suggested-topics-list"]}>
                {suggestedTopics.map((topic, index) => (
                  <div
                    key={index}
                    className={styles["suggested-topic-item"]}
                    onClick={() => handleSuggestedTopicClick(topic)}
                  >
                    {topic}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {messages.map((message, index) => {
            // 判断是否是最后一条助手消息且正在加载
            // 只要正在加载就保持最小高度，直到加载完成（isLoading 变为 false）
            const isLastAssistantLoading = message.role === "assistant" && 
              index === messages.length - 1 && 
              isLoading;
            
            return (
            <div
              key={index}
              data-message-index={index}
              className={`${styles["message"]} ${
                styles[`message-${message.role}`]
              }`}
              style={isLastAssistantLoading ? {
                minHeight: 'calc(100vh - 304px)', // 100vh - header(72px) - input-area(104px) - user消息高度(40px) - margin(32px) - 额外(44px) - 间距(12px)，让用户消息能被顶到顶部
                transition: 'min-height 0.3s ease-out' // 平滑过渡
              } : undefined}
            >
              {message.role === "assistant" && message.stages && message.stages.length > 0 ? (
                <div className={styles["message-content"]}>
                  {(() => {
                    const firstAnalyzingIndex = message.stages.findIndex(s => s.stage === "analyzing");
                    const hasAnalyzing = firstAnalyzingIndex !== -1;
                    const hasProduct = message.stages.some(s => s.stage === "product");
                    const isQueryFinished = !isLoading || index !== messages.length - 1;
                    const shouldCollapseAnalyzing = hasProduct && isQueryFinished;
                    // 使用消息索引判断当前消息的 analyzing 阶段是否展开，每条消息独立控制
                    const isAnalyzingExpanded = expandedAnalyzingStages.has(index);
                    const showAnalyzing = !shouldCollapseAnalyzing || isAnalyzingExpanded;
                    
                    // 切换当前消息（index）的 analyzing 阶段展开状态，不影响其他消息
                    const toggleAnalyzing = () => {
                      setExpandedAnalyzingStages(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(index)) {
                          newSet.delete(index);
                        } else {
                          newSet.add(index);
                        }
                        return newSet;
                      });
                    };
                    
                    return message.stages.map((stageData, stageIndex) => {
                      const isFirstAnalyzingStage = hasAnalyzing && stageIndex === firstAnalyzingIndex;
                      
                      // 如果是第一个 analyzing 阶段，始终显示 header（即使内容被折叠）
                      if (isFirstAnalyzingStage && stageData.stage === "analyzing") {
                        return (
                          <div key={stageIndex} className={styles["stage-item"]}>
                            <div className={styles["stage-header"]}>
                              <span>View the Analysis and Recommendation Process:</span>
                              {shouldCollapseAnalyzing && (
                                <span 
                                  className={styles["toggle-btn"]}
                                  onClick={toggleAnalyzing}
                                >
                                  <Image
                                    src={isAnalyzingExpanded 
                                      ? `${imageDomain}/ai-chat/up.svg`
                                      : `${imageDomain}/ai-chat/down.svg`
                                    }
                                    alt={isAnalyzingExpanded ? "Collapse" : "Expand"}
                                    width={16}
                                    height={16}
                                  />
                                </span>
                              )}
                            </div>
                            {/* 如果 analyzing 展开，才渲染内容 */}
                            {showAnalyzing && (
                              <div className={styles[`stage-${stageData.stage}`]}>
                                <Image
                                  src={`${imageDomain}/ai-chat/successful.svg`}
                                  alt="successful"
                                  width={16}
                                  height={16}
                                  className={styles["stage-icon"]}
                                />
                                <div 
                                  className={styles["stage-content"]}
                                  dangerouslySetInnerHTML={{
                                    __html: typeof stageData.content === "string" 
                                      ? stageData.content 
                                      : JSON.stringify(stageData.content)
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // 如果是其他 analyzing 阶段且应该折叠，则不渲染
                      if (stageData.stage === "analyzing" && !showAnalyzing) {
                        return null;
                      }
                      
                      // 如果是 product stage，content 已经是对象，直接使用
                      if (stageData.stage === "product") {
                        if (typeof stageData.content === "object" && stageData.content !== null) {
                          const productData = stageData.content as any;
                          return (
                            <div key={stageIndex} className={styles["stage-item"]}>
                              <div className={styles[`stage-${stageData.stage}`]}>
                                <GoodsAIRecommend
                                  productID={productData.productID}
                                  name={productData.name}
                                  url={productData.url}
                                  imgUrl={productData.picture}
                                  price={productData.Price || productData.price}
                                  normalPrice={productData.normalPrice || productData.Price || productData.price}
                                  aiRecommendation={productData.reason || ""}
                                  reason_summary={productData.reason_summary}
                                  highlight={productData.highlight || false}
                                />
                              </div>
                            </div>
                          );
                        } else {
                          // 如果 content 不是对象，尝试解析
                          try {
                            const productData = typeof stageData.content === "string" 
                              ? JSON.parse(stageData.content) 
                              : stageData.content;
                            return (
                              <div key={stageIndex} className={styles["stage-item"]}>
                                <div className={styles[`stage-${stageData.stage}`]}>
                                  <GoodsAIRecommend
                                    productID={productData.productID}
                                    name={productData.name}
                                    url={productData.url}
                                    imgUrl={productData.picture}
                                    price={productData.Price || productData.price}
                                    normalPrice={productData.normalPrice || productData.Price || productData.price}
                                    aiRecommendation={productData.reason || ""}
                                  />
                                </div>
                              </div>
                            );
                          } catch (error) {
                            // 如果解析失败，显示原始内容
                            return (
                              <div key={stageIndex} className={styles["stage-item"]}>
                                <div className={styles[`stage-${stageData.stage}`]}>
                                  <div 
                                    className={styles["stage-content"]}
                                    dangerouslySetInnerHTML={{
                                      __html: typeof stageData.content === "string" 
                                        ? stageData.content 
                                        : JSON.stringify(stageData.content)
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                        }
                      }
                      
                      // 如果是 recommend_words stage，显示 title 和 list
                      if (stageData.stage === "recommend_words") {
                        let recommendData: any = null;
                        
                        // 尝试解析 content
                        if (typeof stageData.content === "object" && stageData.content !== null) {
                          recommendData = stageData.content;
                        } else if (typeof stageData.content === "string") {
                          try {
                            recommendData = JSON.parse(stageData.content);
                          } catch (error) {
                            // 解析失败，使用默认值
                            recommendData = null;
                          }
                        }
                        
                        if (recommendData && recommendData.title) {
                          // 找到对应的用户消息（通常是前一条消息）
                          const userMessageIndex = index > 0 && messages[index - 1].role === "user" 
                            ? index - 1 
                            : null;
                          const userQuery = userMessageIndex !== null ? messages[userMessageIndex].content : "";
                          const refreshKey = `refresh-${index}-${stageIndex}`;
                          
                          return (
                            <Fragment key={stageIndex}>
                              <div className={styles["stage-item"]}>
                                <div className={styles[`stage-${stageData.stage}`]}>
                                  <div className={styles["recommend-words-title"]}>
                                    {recommendData.title}
                                  </div>
                                  {recommendData.list && Array.isArray(recommendData.list) && recommendData.list.length > 0 && (
                                    <>
                                      {recommendData.list.map((item: string, itemIndex: number) => (
                                        <span 
                                          key={itemIndex} 
                                          className={styles["recommend-words-item"]}
                                          onClick={() => {
                                            matomoSetCustomDimension(43, item);
                                            handleSendWithQuery(item);
                                          }}
                                        >
                                          {item}
                                        </span>
                                      ))}
                                    </>
                                  )}
                                </div>
                              </div>
                              {/* 商品由AI生成的提示 */}
                              <div className={styles["ai-generated-hint"]}>
                                <div 
                                  className={styles["refresh-icon-wrapper"]}
                                  onMouseEnter={() => setRefreshHoverKey(refreshKey)}
                                  onMouseLeave={() => setRefreshHoverKey(null)}
                                  onClick={() => {
                                    if (userQuery) {
                                      handleSendWithQuery(userQuery, true);
                                    }
                                  }}
                                >
                                  <Image
                                    src={refreshHoverKey === refreshKey 
                                      ? `${imageDomain}/ai-chat/refresh-hover.svg`
                                      : `${imageDomain}/ai-chat/refresh.svg`
                                    }
                                    alt="Refresh"
                                    width={16}
                                    height={16}
                                    className={styles["refresh-icon"]}
                                  />
                                </div>
                                <span className={styles["ai-generated-text"]}>
                                  This content is created by AI.
                                </span>
                              </div>
                            </Fragment>
                          );
                        }
                        
                        // 如果解析失败或数据格式不正确，显示原始内容
                        // 找到对应的用户消息（通常是前一条消息）
                        const userMessageIndex = index > 0 && messages[index - 1].role === "user" 
                          ? index - 1 
                          : null;
                        const userQuery = userMessageIndex !== null ? messages[userMessageIndex].content : "";
                        const refreshKey = `refresh-${index}-${stageIndex}`;
                        
                        return (
                          <Fragment key={stageIndex}>
                            <div className={styles["stage-item"]}>
                              <div className={styles[`stage-${stageData.stage}`]}>
                                <div 
                                  className={styles["stage-content"]}
                                  dangerouslySetInnerHTML={{
                                    __html: typeof stageData.content === "string" 
                                      ? stageData.content 
                                      : JSON.stringify(stageData.content)
                                  }}
                                />
                              </div>
                            </div>
                            {/* 商品由AI生成的提示 */}
                            <div className={styles["ai-generated-hint"]}>
                              <div 
                                className={styles["refresh-icon-wrapper"]}
                                onMouseEnter={() => setRefreshHoverKey(refreshKey)}
                                onMouseLeave={() => setRefreshHoverKey(null)}
                                onClick={() => {
                                  if (userQuery) {
                                    handleSendWithQuery(userQuery, true);
                                  }
                                }}
                              >
                                <Image
                                  src={refreshHoverKey === refreshKey 
                                    ? `${imageDomain}/ai-chat/refresh-hover.svg`
                                    : `${imageDomain}/ai-chat/refresh.svg`
                                  }
                                  alt="Refresh"
                                  width={16}
                                  height={16}
                                  className={styles["refresh-icon"]}
                                />
                              </div>
                              <span className={styles["ai-generated-text"]}>
                                This content is generated by AI.
                              </span>
                            </div>
                          </Fragment>
                        );
                      }
                      
                      // 其他 analyzing 阶段（非第一个）或非 analyzing 阶段
                      return (
                        <div key={stageIndex} className={styles["stage-item"]}>
                          <div className={styles[`stage-${stageData.stage}`]}>
                            {stageData.stage === "analyzing" && (
                              <Image
                                src={`${imageDomain}/ai-chat/successful.svg`}
                                alt="successful"
                                width={16}
                                height={16}
                                className={styles["stage-icon"]}
                              />
                            )}
                            <div 
                              className={styles["stage-content"]}
                              dangerouslySetInnerHTML={{
                                __html: typeof stageData.content === "string" 
                                  ? stageData.content 
                                  : JSON.stringify(stageData.content)
                              }}
                            />
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {isLoading && index === messages.length - 1 && (
                    <Spin size="small" />
                  )}
                </div>
              ) : (
                <div className={styles["message-content"]}>
                  {message.content ? (
                    <div dangerouslySetInnerHTML={{ __html: message.content }} />
                  ) : (
                    isLoading && index === messages.length - 1 ? (
                      <Spin size="small" />
                    ) : null
                  )}
                </div>
              )}
            </div>
            );
          })}
          {showNewChatDivider && messages.length > 0 && (
            <div className={styles["new-chat-divider"]}>
              <div className={styles["divider-line"]}></div>
              <div className={styles["divider-content"]}>
                <Image
                  src={`${imageDomain}/ai-chat/chat.svg`}
                  alt="New Chat"
                  width={16}
                  height={16}
                  className={styles["divider-icon"]}
                />
                <span className={styles["divider-text"]}>
                  Let&apos;s chat about something new!
                </span>
              </div>
              <div className={styles["divider-line"]}></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        <div className={styles["ai-search-input-area"]}>
          <div style={{ position: 'relative' }}>
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder=""
              disabled={isLoading}
              className={styles["input"]}
              rows={2}
              autoSize={{ minRows: 2, maxRows: 2 }}
            />
            {!inputValue && (
              <div 
                className={styles["input-placeholder"]}
                dangerouslySetInnerHTML={{ __html: "Describe what you want to buy…" }}
              />
            )}
          </div>
          <div className={styles["input-actions"]}>
            <Image
              src={isNewChatHovered && !isLoading ? `${imageDomain}/ai-chat/newchat-hover.svg` : `${imageDomain}/ai-chat/newchat.svg`}
              alt="New Chat"
              width={16}
              height={16}
              className={`${styles["new-chat-btn"]} ${isLoading ? styles["new-chat-btn-disabled"] : ""}`}
              onClick={() => {
                if (!isLoading) {
                  handleClear();
                }
              }}
              onMouseEnter={() => {
                if (!isLoading) {
                  setIsNewChatHovered(true);
                }
              }}
              onMouseLeave={() => setIsNewChatHovered(false)}
            />
            <div 
              className={`${styles["send-btn-box"]} ${
                !inputValue.trim() || isLoading ? styles["send-btn-box-disabled"] : ""
              }`}
            >
              {isLoading ? (
                <LoadingOutlined 
                  className={styles["send-btn"]}
                  style={{
                    fontSize: 18,
                    color: '#FFFFFF'
                  }}
                />
              ) : (
                <Image
                  src={`${imageDomain}/ai-chat/send.svg`}
                  alt="Send"
                  width={18}
                  height={18}
                  className={styles["send-btn"]}
                  onClick={() => {
                    if (inputValue.trim() && !isLoading) {
                      handleSend();
                    }
                  }}
                  style={{
                    cursor: !inputValue.trim() || isLoading ? 'not-allowed' : 'pointer'
                  }}
                />
              )}
            </div>
            
          </div>
        </div>
      </div>
    </>
  );
}
